import { getSupabase } from './client';
import type { JsonObject } from './database.types';
import { toDataError } from './errors';
import { asJsonObject, jsonEqual } from './json';
import type { WorkspaceScope } from './workspace';

export interface JsonPatch {
  readonly set: JsonObject;
  readonly unset: string[];
}

/** Top-level key diff so concurrent edits of different settings do not overwrite each other. */
export function diffTopLevel(previous: JsonObject, next: JsonObject): JsonPatch {
  const set: JsonObject = {};
  const unset: string[] = [];
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined) continue;
    if (!jsonEqual(previous[key], value)) set[key] = value;
  }
  for (const key of Object.keys(previous)) {
    if (previous[key] !== undefined && next[key] === undefined) unset.push(key);
  }
  return { set, unset };
}

export function isEmptyPatch(patch: JsonPatch): boolean {
  return Object.keys(patch.set).length === 0 && patch.unset.length === 0;
}

/**
 * Shared dashboard settings (employee master, menus, categories, report defaults …).
 * EMPLOYEE-PC SYNC FIX: read from Postgres on every client load instead of localStorage.
 */
export async function fetchSettings(scope: WorkspaceScope): Promise<JsonObject> {
  const { data, error } = await getSupabase()
    .from('workspace_settings')
    .select('data')
    .eq('workspace_id', scope.workspaceId)
    .eq('app_key', scope.appKey)
    .maybeSingle();
  if (error) throw toDataError(error);
  return asJsonObject(data?.data);
}

export async function patchSettings(scope: WorkspaceScope, patch: JsonPatch): Promise<JsonObject> {
  const { data, error } = await getSupabase().rpc('settings_patch', {
    p_workspace: scope.workspaceId,
    p_app: scope.appKey,
    p_set: patch.set,
    p_unset: patch.unset,
  });
  if (error) throw toDataError(error);
  return asJsonObject(data);
}

/** Admins receive the stored tokens; for everyone else RLS returns no row → {}. */
export async function fetchSecrets(scope: WorkspaceScope): Promise<JsonObject> {
  const { data, error } = await getSupabase()
    .from('workspace_secrets')
    .select('data')
    .eq('workspace_id', scope.workspaceId)
    .eq('app_key', scope.appKey)
    .maybeSingle();
  if (error) throw toDataError(error);
  return asJsonObject(data?.data);
}

export async function patchSecrets(scope: WorkspaceScope, patch: JsonPatch): Promise<JsonObject> {
  const { data, error } = await getSupabase().rpc('secrets_patch', {
    p_workspace: scope.workspaceId,
    p_app: scope.appKey,
    p_set: patch.set,
    p_unset: patch.unset,
  });
  if (error) throw toDataError(error);
  return asJsonObject(data);
}
